import { useState, useEffect, useRef } from 'react';

export type AutoLoopState = 
  'IDLE' | 
  'WAITING_FOR_GEN' | 'WAITING_FOR_AUDIT' | 
  'PRO_GEN' | 'PRO_SEL' | 'CON_GEN' | 'CON_SEL' | 'JUDGE' | 'ITERATE' | 
  'DETAIL_GEN' | 'DETAIL_AUDIT' | 'DETAIL_ITERATE' | 
  'LESSON_PLAN_P1' | 'LESSON_PLAN_P2' | 'LESSON_PLAN_P3' | 'LESSON_PLAN_P4' | 'LESSON_PLAN_P5' | 'LESSON_PLAN_P6';

export type ToastMessage = {
  text: string;
  type: 'success' | 'warning';
} | null;

const PHASE_NAMES = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];

const getLessonPartText = (phaseCount: number, partIndex: number): string => {
  if (phaseCount === 10) {
    switch (partIndex) {
      case 2: return '继续仅返回完整详细教案的第二部分（环节一~三）';
      case 3: return '继续仅返回完整详细教案的第三部分（环节四~五）';
      case 4: return '继续仅返回完整详细教案的第四部分（环节六~七）';
      case 5: return '继续仅返回完整详细教案的第五部分（环节八~九）';
      case 6: return '继续仅返回完整详细教案的第六部分（环节十及之后剩余内容）';
      default: return `继续仅返回完整详细教案的第${PHASE_NAMES[partIndex - 1]}部分`;
    }
  }
  return `继续仅返回完整详细教案的第${PHASE_NAMES[partIndex - 1]}部分`;
};

export const useAgentLoop = (
  isGenerating: boolean,
  messages: { role: string; content: string }[],
  onDirectSend: (text: string) => void,
  onAppendContent?: (text: string) => void,
  onSaveContent?: () => void,
  phaseCount: number = 7
) => {
  const [autoLoopState, setAutoLoopState] = useState<AutoLoopState>('IDLE');
  const [currentLoopPhase, setCurrentLoopPhase] = useState<string>('一'); 
  const [loopCount, setLoopCount] = useState(0);
  const [toastMessage, setToastMessage] = useState<ToastMessage>(null);
  const prevGeneratingRef = useRef(isGenerating);

  const totalParts = phaseCount === 10 ? 6 : 5;
  const MAX_LOOPS = 5;   
  const PASS_SCORE_PLAN = 95; 
  const PASS_SCORE_PHASE = 90;   
  const PASS_SCORE_DETAIL = 90; 

  useEffect(() => {
    const justFinished = prevGeneratingRef.current && !isGenerating;
    prevGeneratingRef.current = isGenerating;

    if (justFinished && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      
      if (lastMessage.role === 'assistant') {
        const content = lastMessage.content;
        
        const parseScore = (text: string) => {
          const scoreMatch = text.match(/\[FINAL_SCORE:\s*(\d+)\]/i);
          if (scoreMatch && scoreMatch[1]) return parseInt(scoreMatch[1], 10);
          const fallbackMatch = text.match(/总分\s*(\d+)/) || text.match(/得分[：:]\s*(\d+)/);
          if (fallbackMatch && fallbackMatch[1]) return parseInt(fallbackMatch[1], 10);
          return 0;
        };

        // ==========================================
        // 【逻辑链 A：整体方案自动流转】
        // ==========================================
        if (autoLoopState === 'WAITING_FOR_GEN') {
          setTimeout(() => { setAutoLoopState('WAITING_FOR_AUDIT'); onDirectSend('审核整体方案设计'); }, 1500); 
        } 
        else if (autoLoopState === 'WAITING_FOR_AUDIT') {
          const score = parseScore(content);
          if (score >= PASS_SCORE_PLAN) { setAutoLoopState('IDLE'); setLoopCount(0); } 
          else if (loopCount + 1 >= MAX_LOOPS) { setAutoLoopState('IDLE'); setLoopCount(0); setToastMessage({ text: "整体方案迭代达上限，请人工介入。", type: 'warning' }); } 
          else {
            setTimeout(() => { setLoopCount(prev => prev + 1); setAutoLoopState('WAITING_FOR_GEN'); onDirectSend('执行调整并输出优化后的整体方案设计'); }, 1500);
          }
        }
        
        // ==========================================
        // 【逻辑链 B：通用环节多智能体对抗流转 (x1.1 - x1.6)】
        // ==========================================
        else if (autoLoopState === 'PRO_GEN') {
          setTimeout(() => { setAutoLoopState('PRO_SEL'); onDirectSend(`环节${currentLoopPhase}正方最优方案`); }, 1500);
        }
        else if (autoLoopState === 'PRO_SEL') {
          setTimeout(() => { setAutoLoopState('CON_GEN'); onDirectSend(`环节${currentLoopPhase}反方3个备选方案`); }, 1500);
        }
        else if (autoLoopState === 'CON_GEN') {
          setTimeout(() => { setAutoLoopState('CON_SEL'); onDirectSend(`环节${currentLoopPhase}反方最优方案`); }, 1500);
        }
        else if (autoLoopState === 'CON_SEL') {
          setTimeout(() => { setAutoLoopState('JUDGE'); onDirectSend(`环节${currentLoopPhase}第三方评审`); }, 1500);
        }
        else if (autoLoopState === 'ITERATE') {
          setTimeout(() => { setAutoLoopState('JUDGE'); onDirectSend(`环节${currentLoopPhase}第三方重新评审`); }, 1500);
        }
        else if (autoLoopState === 'JUDGE') {
          const score = parseScore(content);
          if (score >= PASS_SCORE_PHASE) { setAutoLoopState('IDLE'); setLoopCount(0); } 
          else if (loopCount + 1 >= MAX_LOOPS) { setAutoLoopState('IDLE'); setLoopCount(0); setToastMessage({ text: `环节${currentLoopPhase}对抗迭代达上限，请人工介入。`, type: 'warning' }); } 
          else {
            setTimeout(() => { setLoopCount(prev => prev + 1); setAutoLoopState('ITERATE'); onDirectSend(`环节${currentLoopPhase}专家吸收意见重构方案`); }, 1500);
          }
        }

        // ==========================================
        // 【逻辑链 C：通用环节详细设计自动打分流转 (x2.1 - x2.3)】
        // ==========================================
        else if (autoLoopState === 'DETAIL_GEN' || autoLoopState === 'DETAIL_ITERATE') {
          setTimeout(() => { setAutoLoopState('DETAIL_AUDIT'); onDirectSend(`环节${currentLoopPhase}审核详细方案`); }, 1500);
        }
        else if (autoLoopState === 'DETAIL_AUDIT') {
          const score = parseScore(content);
          if (score >= PASS_SCORE_DETAIL) { setAutoLoopState('IDLE'); setLoopCount(0); } 
          else if (loopCount + 1 >= MAX_LOOPS) { setAutoLoopState('IDLE'); setLoopCount(0); setToastMessage({ text: `环节${currentLoopPhase}详细设计迭代达上限。请人工介入。`, type: 'warning' }); } 
          else {
            setTimeout(() => { setLoopCount(prev => prev + 1); setAutoLoopState('DETAIL_ITERATE'); onDirectSend(`环节${currentLoopPhase}优化详细方案`); }, 1500);
          }
        }

        // ==========================================
        // 【逻辑链 D：完整详细教案分多步自动生成】
        // phaseCount=7: 5个分段 (P1-P5)
        // phaseCount=10: 6个分段 (P1-P6)
        // ==========================================
        else if (autoLoopState.startsWith('LESSON_PLAN_P')) {
            if (onAppendContent) onAppendContent(content);

            if (autoLoopState === 'LESSON_PLAN_P1') {
                setTimeout(() => { setAutoLoopState('LESSON_PLAN_P2'); setLoopCount(1); onDirectSend(getLessonPartText(phaseCount, 2)); }, 1500);
            }
            else if (autoLoopState === 'LESSON_PLAN_P2') {
                setTimeout(() => { setAutoLoopState('LESSON_PLAN_P3'); setLoopCount(2); onDirectSend(getLessonPartText(phaseCount, 3)); }, 1500);
            }
            else if (autoLoopState === 'LESSON_PLAN_P3') {
                setTimeout(() => { setAutoLoopState('LESSON_PLAN_P4'); setLoopCount(3); onDirectSend(getLessonPartText(phaseCount, 4)); }, 1500);
            }
            else if (autoLoopState === 'LESSON_PLAN_P4') {
                setTimeout(() => { setAutoLoopState('LESSON_PLAN_P5'); setLoopCount(4); onDirectSend(getLessonPartText(phaseCount, 5)); }, 1500);
            }
            else if (autoLoopState === 'LESSON_PLAN_P5') {
                if (totalParts === 6) {
                    setTimeout(() => { setAutoLoopState('LESSON_PLAN_P6'); setLoopCount(5); onDirectSend(getLessonPartText(phaseCount, 6)); }, 1500);
                } else {
                    setTimeout(() => {
                        if (onSaveContent) onSaveContent();
                        setAutoLoopState('IDLE'); 
                        setLoopCount(0); 
                    }, 500);
                }
            }
            else if (autoLoopState === 'LESSON_PLAN_P6') {
                setTimeout(() => {
                    if (onSaveContent) onSaveContent();
                    setAutoLoopState('IDLE'); 
                    setLoopCount(0); 
                }, 500);
            }
        }
      }
    }
  }, [isGenerating, messages, autoLoopState, loopCount, currentLoopPhase, onDirectSend, onAppendContent, onSaveContent, phaseCount]);

  const getStatusText = () => {
    if (autoLoopState.includes('WAITING')) return '整体方案：正在执行闭环...';
    
    // 通用环节文字反馈
    if (autoLoopState === 'PRO_GEN') return `环节${currentLoopPhase}博弈：正方发散方案...`;
    if (autoLoopState === 'PRO_SEL') return `环节${currentLoopPhase}博弈：正方收敛方案...`;
    if (autoLoopState === 'CON_GEN') return `环节${currentLoopPhase}博弈：反方踢馆颠覆...`;
    if (autoLoopState === 'CON_SEL') return `环节${currentLoopPhase}博弈：反方最强一击...`;
    if (autoLoopState === 'JUDGE') return `环节${currentLoopPhase}博弈：委员会残酷取证与评审...`;
    if (autoLoopState === 'ITERATE') return `环节${currentLoopPhase}博弈：吸收建议定向重构中...`;
    
    if (autoLoopState === 'DETAIL_GEN') return `环节${currentLoopPhase}剧本：撰写与映射中...`;
    if (autoLoopState === 'DETAIL_AUDIT') return `环节${currentLoopPhase}剧本：红线与违禁审查中...`;
    if (autoLoopState === 'DETAIL_ITERATE') return `环节${currentLoopPhase}剧本：定向修复硬伤中...`;

    // 教案分段输出反馈
    const totalParts = phaseCount === 10 ? 6 : 5;
    if (autoLoopState === 'LESSON_PLAN_P1') return `教案生成：正在输出第 1/${totalParts} 部分...`;
    if (autoLoopState === 'LESSON_PLAN_P2') return `教案生成：正在输出第 2/${totalParts} 部分...`;
    if (autoLoopState === 'LESSON_PLAN_P3') return `教案生成：正在输出第 3/${totalParts} 部分...`;
    if (autoLoopState === 'LESSON_PLAN_P4') return `教案生成：正在输出第 4/${totalParts} 部分...`;
    if (autoLoopState === 'LESSON_PLAN_P5') return `教案生成：正在输出第 5/${totalParts} 部分${totalParts === 5 ? ' (收尾)' : '...'}...`;
    if (autoLoopState === 'LESSON_PLAN_P6') return `教案生成：正在输出第 6/6 部分 (收尾)...`;

    return '';
  };

  return { autoLoopState, setAutoLoopState, currentLoopPhase, setCurrentLoopPhase, loopCount, setLoopCount, MAX_LOOPS, totalParts, getStatusText, toastMessage, setToastMessage };
};